import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithColumns,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementHorizontalAlignment,
  type ElementSizingMode,
  type FontOptions,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
  type PropertiesConfigItemDefinition,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { TabsDefinitionMapper } from './ai/tabs.definition-mapper';
import { TabsElementHandler } from './ai/tabs.element-handler';
import { tabsV2Migration } from './migrations/002-tabs.migration';
import { TabsComponent } from './tabs.component';
import {
  MAX_FIXED_TAB_WIDTH,
  MIN_FIXED_TAB_WIDTH,
  type TabIconPosition,
  type TabItem,
  TABS_TEMPLATE_OPTIONS,
  type TabSize,
  type TabsTemplate,
} from './tabs.model';

export type TabsDef = WithColumns & WithProps<TabsPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    tabs: TabsDef;
  }
}

const DEFAULT_WIDTH = 720;
const DEFAULT_HEIGHT = 380;
const MIN_SIZE = 100;
const MAX_WIDTH = 900;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];

registerElementDefinition({
  type: 'tabs',
  componentType: TabsComponent,
  isListed: true,
  name: 'Tabs',
  groups: ['containers'],
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  // By default the element should have a min-height and grow automatically if needed
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  slots: {
    ids: ['0', '1', '2'],
    0: { children: [] },
    1: { children: [] },
    2: { children: [] },
  },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  styleIncludePaths: ['style'],
  styleExcludePaths: ['style.tabs.tabsView'],
  features: {
    preventAutoWidth: true,
    enforceAllConnectionsWithinSlot: true,
  },
  version: 2,
  migrations: {
    2: tabsV2Migration,
  },
  ai: {
    name: 'tabs',
    handler: () => TabsElementHandler,
    definitionMapper: () => TabsDefinitionMapper,
  },
});

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type TabsPropsModel = PropsModel & {
  content: {
    options: {
      template: TabsTemplate;
      tabSize: TabSize;
      /** Used when tab size is fixed */
      tabWidth: ValueWithUnitOfMeasure;
      align: ElementHorizontalAlignment;
      iconPosition: TabIconPosition;
    };
    tabs: {
      items: TabItem[];
    };
  };
  style: {
    general: {
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
      background: PropertiesConfigInputColorModel;
      outline: PropertiesConfigInputColorModel;
      outlineWidth: ValueWithUnitOfMeasure;
      radius: BorderRadiusValue;
      visualEffect: PropertiesConfigInputVisualEffectModel;
    };
    font: FontOptions;
    tabs: {
      tabsView: 'default' | 'selected';
      default: {
        label: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
        fillHover: PropertiesConfigInputColorModel;
      };
      selected: {
        label: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
        fillHover: PropertiesConfigInputColorModel;
      };
      /** A variant used for the `line` template */
      selectedLine: {
        label: PropertiesConfigInputColorModel;
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
        fillHover: PropertiesConfigInputColorModel;
      };
    };
  };
};

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [],
  };
}

function getDefaults(): PropsOf<'tabs'> {
  return {
    content: {
      options: {
        template: 'line',
        tabSize: 'hug',
        tabWidth: '160px',
        align: 'center',
        iconPosition: 'left',
      },
      tabs: {
        items: [],
      },
    },
    style: {
      general: {
        offset: { margin: '$t.spacingExternal', padding: '' },
        background: { color1: '$t.fill', selectorType: 'none' },
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      font: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontWeight: '$t.fontWeight' as FontWeightOption,
        lineHeight: '$t.lineHeight',
        fontSize: '14px', // 14? // '$t.fontSize' ?
      },
      tabs: {
        tabsView: 'default',
        default: {
          label: { color1: '$t.title', selectorType: 'solid' },
          fill: { color1: '$t.fill', selectorType: 'solid' },
          outline: { color1: '$t.outline', selectorType: 'solid' },
          fillHover: { color1: '$t.complementary', selectorType: 'solid' },
        },
        selected: {
          label: { color1: '$t.accentLabel', selectorType: 'solid' },
          fill: { color1: '$t.accent', selectorType: 'solid' },
          outline: { color1: '$t.accent', selectorType: 'solid' },
          fillHover: { color1: '$t.accent', selectorType: 'solid' },
        },
        selectedLine: {
          label: { color1: '$t.accent', selectorType: 'solid' },
          fill: { color1: '$t.fill', selectorType: 'solid' },
          outline: { color1: '$t.accent', selectorType: 'solid' },
          fillHover: { color1: '$t.fill', selectorType: 'solid' },
        },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'tabs'> {
  return {
    content: {
      tabs: {
        items: [
          { id: 0, label: 'Tab 1' },
          { id: 1, label: 'Tab 2' },
          { id: 2, label: 'Tab 3' },
        ],
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('tabs', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.tabs',
        'Tabs',
        [tabsField('content.tabs.items', 'Tabs')],
        { hideLabel: true },
      ),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),

      epc.subsections.visibilitySubsection(),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('content.options', 'Options', [
          epc.fields.selectFieldFromImageList(
            'tabs-template',
            'content.options.template',
            'Template',
            TABS_TEMPLATE_OPTIONS,
            { allowNone: false, showSelection: true },
          ),
          epc.fields.radiosField('content.options.tabSize', 'Tab size', [
            { value: 'hug', label: 'Fit' },
            { value: 'fill', label: 'Full' },
            { value: 'fixed', label: 'Fixed' },
          ]),
          epc.utils.conditionalField(
            epc.fields.sliderField('content.options.tabWidth', 'Width', {
              units: [{ min: MIN_FIXED_TAB_WIDTH, max: MAX_FIXED_TAB_WIDTH, step: 1, unit: 'px' }],
              hideSpinners: true, // Hide spinner to make more space for 3-digit values
            }),
            'content.options.tabSize',
            (tabSize) => tabSize !== 'fixed',
          ),
          epc.utils.conditionalField(
            epc.fields.horizontalAlignField('content.options.align', { label: 'Align' }),
            'content.options.tabSize',
            (tabSize) => tabSize === 'fill',
          ),
          epc.fields.radiosField('content.options.iconPosition', 'Icon position', [
            { value: 'left', label: 'Left' },
            { value: 'top', label: 'Top' },
          ]),
        ]),
        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.background', 'Fill'),
          epc.fields.colorField('style.general.outline', 'Outline', {
            allowGradient: false,
            hasOutlineSides: true,
          }),
          epc.fields.sliderField('style.general.outlineWidth', 'Width', [
            { min: 0, max: 10, unit: 'px' },
          ]),
          epc.fields.radiusField('style.general.radius', 'Radius'),
          ...epc.fields.visualEffectField('style.general.visualEffect'),
        ]),
        epc.subsections.elementFontSubsection('style.font'),
        epc.subsections.subsection('style.tabs', 'Tabs', [
          epc.fields.radiosField(
            'style.tabs.tabsView',
            'Tabs view',
            [
              { value: 'default', label: 'Default' },
              { value: 'selected', label: 'Selected' },
            ],
            { hideLabel: true },
          ),

          // Default tab style for all templates
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.tabs.default.label', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.tabs.default.fill', 'Fill'),
              // Outline for `box` and `header` templates
              epc.utils.conditionalField(
                epc.fields.colorField('style.tabs.default.outline', 'Outline', {
                  allowGradient: false,
                }),
                'content.options.template',
                (template: TabsPropsModel['content']['options']['template']) =>
                  template !== 'box' && template !== 'header',
              ),
              // Line for `line` template
              epc.utils.conditionalField(
                epc.fields.colorField('style.tabs.default.outline', 'Line', {
                  allowGradient: false,
                }),
                'content.options.template',
                (template: TabsPropsModel['content']['options']['template']) => template !== 'line',
              ),
              epc.utils.separatedField(
                epc.fields.colorField('style.tabs.default.fillHover', 'Fill : Hover'),
                { hideSeparatorWhenHidden: true },
              ),
            ],
            'style.tabs.tabsView',
            (tabsView: TabsPropsModel['style']['tabs']['tabsView']) => tabsView !== 'default',
          ),

          // Selected tab style for most templates
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.tabs.selected.label', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.tabs.selected.fill', 'Fill'),
              // Outline for `box` and `header` templates
              epc.utils.conditionalField(
                epc.fields.colorField('style.tabs.selected.outline', 'Outline', {
                  allowGradient: false,
                }),
                'content.options.template',
                (template: TabsPropsModel['content']['options']['template']) =>
                  template !== 'box' && template !== 'header',
              ),
              epc.utils.separatedField(
                epc.fields.colorField('style.tabs.selected.fillHover', 'Fill : Hover'),
                { hideSeparatorWhenHidden: true },
              ),
            ],
            '',
            (_, props: TabsPropsModel) =>
              props.style.tabs.tabsView !== 'selected' || props.content.options.template === 'line',
          ),

          // Selected tab style for the `line` template
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.tabs.selectedLine.label', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.tabs.selectedLine.fill', 'Fill'),
              epc.fields.colorField('style.tabs.selectedLine.outline', 'Line', {
                allowGradient: false,
              }),
              epc.utils.separatedField(
                epc.fields.colorField('style.tabs.selectedLine.fillHover', 'Fill : Hover'),
                { hideSeparatorWhenHidden: true },
              ),
            ],
            '',
            (_, props: TabsPropsModel) =>
              props.style.tabs.tabsView !== 'selected' || props.content.options.template !== 'line',
          ),
        ]),
      ],
      true,
    ),
  ]);
}

function tabsField(path: string, label: string): PropertiesConfigItemDefinition<'collectionList'> {
  return {
    path,
    inputType: 'collectionList',
    label,
    inputOptions: {
      fieldAction: 'COLLECTION_EDIT_TAB',
      fieldPaneTitle: 'Edit Tab',
      fieldConfig: (index) => [
        epc.subsections.subsection(
          `${path}.${index}`,
          '',
          [
            epc.fields.textInput(`${path}.${index}.label`, 'Label'),
            epc.fields.iconField(`${path}.${index}.icon`, 'Icon'),
          ],
          { hideLabel: true, classes: ['break-top'] },
        ),

        epc.subsections.subsection(
          `${path}.${index}`,
          'Visibility',
          [
            epc.fields.viewportVisibilityField(`${path}.${index}.visible.viewports`, 'Show on'),
            epc.utils.separator('', { labelOnLine: 'and' }),
            epc.fields.visibilityField('', `${path}.${index}`),
          ],
          { hideLabel: true, classes: ['break-bottom'] },
        ),
      ],
      fieldFactory: ({ id }): TabItem => ({
        id,
        label: `Tab ${id + 1}`,
      }),
      selectable: true,
    },
  };
}
